import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/Spinner';
import styles from './ClientForms.module.scss';
import ClientFormsTable, { ClientFormRecord } from './Table/ClientFormsTable';
import ViewCompletedForm from './CompletedForms';
import { RootState } from 'store';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import { listFormInvitationItemsThunk, listFormsThunk, getFormInvitationItemThunk } from '@spike/forms-action';
import type { FormInvitationItemShow } from '@spike/form-model';
import Client from '@spike/client-model';
import { SendForms } from './SendForms';

interface Props {
    clientId: number;
    client: Client;
    onBook: () => void;
    onNewSale?: () => void;
    onClose: () => void;
    onSelect: (key: string) => void;
    /**
     * Threaded to `ViewCompletedForm` to suppress its mobile-only Client Profile header.
     * Client Profile omits it and keeps that header.
     */
    hideClientHeader?: boolean;
}

const ClientForms: React.FC<Props> = ({ clientId, client, onBook, onNewSale, onClose, onSelect, hideClientHeader }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper() as any;

    // Reuse forms reducer; invitation items are stored here by the updated library
    const formsSlice: any = useSelector((s: RootState) => (s as any).forms);
    const loadingFromSlice: boolean = formsSlice?.loading ?? false;
    const dataFromSlice: any[] = ((): any[] => {
        // Solo usamos formInvitationItems, que es donde la librería coloca estos datos
        const s: any = formsSlice as any;
        if (Array.isArray(s?.formInvitationItems)) return s.formInvitationItems as any[];
        return [];
    })();

    const [initialLoading, setInitialLoading] = React.useState(true);
    const [searchText, setSearchText] = React.useState('');
    // UI tabs: 'pending' | 'completed'. Backend expects 'sent' | 'completed'.
    const [tab, setTab] = React.useState<'pending' | 'completed'>('pending');
    const [page, setPage] = React.useState(1);
    const [size, setSize] = React.useState(10);
    // When viewing a completed form, replace the list with the full page view
    const [viewingRecord, setViewingRecord] = React.useState<ClientFormRecord | null>(null);
    const [viewingData, setViewingData] = React.useState<FormInvitationItemShow | null>(null);
    const [viewingLoading, setViewingLoading] = React.useState(false);
    const [showSendForms, setShowSendForms] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                // Map UI tab to backend status param
                const statusParam = tab === 'pending' ? 'sent,in_progress' : 'completed';
                await (dispatch as any)(
                    (listFormInvitationItemsThunk as any)(apiClientWrapper, {
                        page,
                        per: size,
                        query: searchText.trim() || undefined,
                        status: statusParam,
                        customerId: String(clientId)
                    })
                );
            } finally {
                if (!cancelled) setInitialLoading(false);
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
        // Important: Do not include `apiClientWrapper` in deps — its identity changes on each store update
        // causing an infinite loop of requests. Use only stable primitives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, tab, searchText, page, size]);

    // Ensure we have a map of formId -> form.title to display names; fetch once if empty
    React.useEffect(() => {
        if (Array.isArray((formsSlice as any)?.forms) && (formsSlice as any).forms.length > 0) return;
        const run = async () => {
            try {
                await (dispatch as any)(
                    (listFormsThunk as any)(apiClientWrapper, {
                        page: 1,
                        per: 100
                    })
                );
            } catch {
                // ignore; fallback will use generic name
            }
        };
        void run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formTitleById: Record<string, string> = React.useMemo(() => {
        const map: Record<string, string> = {};
        const arr: any[] = Array.isArray((formsSlice as any)?.forms) ? (formsSlice as any).forms : [];
        for (const f of arr) {
            const id = String(f.id ?? f.formId ?? '');
            const title = f.title ?? f.formTitle ?? f.name;
            if (id && title) map[id] = title;
        }
        return map;
    }, [formsSlice]);

    // Map invitation item DTO -> table-friendly record
    const recordsAll: ClientFormRecord[] = React.useMemo(() => {
        const mapped = (dataFromSlice || []).map((raw: any) => {
            const it = raw?.form_invitation_item || raw?.invitationItem || raw; // flatten if wrapped
            // Build a stable, unique id for row keys. formId may repeat across invitations.
            const compositeKey = `${it.formId ?? 'f'}-${it.customerId ?? 'c'}-${it.createdAt ?? it.updatedAt ?? ''}`;
            const id = it.form_item_id || it.invitation_id || it.id || it.responseId || compositeKey;
            const titleFromMap = formTitleById[String(it.formId ?? '')];
            const title =
                it.form_title ??
                it.title ??
                it.formTitle ??
                titleFromMap ??
                it.name ??
                (it.formId ? `Form #${it.formId}` : undefined);
            const name = title ?? it.name ?? it.formName;
            const sentAt =
                it.sent_at ?? it.invitation_created_at ?? it.created_at ?? it.sentAt ?? it.dateSent ?? it.createdAt;
            const completedAt = it.completed_at ?? it.completedAt ?? it.dateCompleted ?? null;
            const channel = it.sent_via ?? it.channel ?? it.deliveryChannel ?? it.channel_type ?? it.sentVia ?? null;
            const statusRaw = it.form_item_status ?? it.invitation_status ?? it.status;
            const status: 'pending' | 'completed' =
                statusRaw === 'completed' || !!completedAt ? 'completed' : 'pending';
            return {
                id,
                name,
                title,
                form: it.form ?? (title ? { title } : undefined),
                formId: it.formId,
                invitationId: it.invitationId ?? it.invitation_id,
                sentAt,
                dateSent: sentAt,
                completedAt,
                channel,
                status,
                sentVia: it.sentVia,
                token: it.token
            } as ClientFormRecord;
        });

        return mapped;
    }, [dataFromSlice]);

    const fiPagination = (formsSlice as any)?.formInvitationItemsPagination || {};
    const currentPage = (fiPagination.page as number | undefined) ?? page;
    const totalPages = (fiPagination.totalPages as number | undefined) ?? 1;
    const pageSizeFromSlice = (fiPagination.size as number | undefined) ?? size;

    // Backend already paginates invitation items; show them as-is for the current page
    const records: ClientFormRecord[] = React.useMemo(() => recordsAll, [recordsAll]);

    const handleShowSendForms = () => {
        setShowSendForms(true);
    };

    const handleCloseShareSendClients = () => {
        setShowSendForms(false);
    };

    const handleSendForms = () => {
        const statusParam = tab === 'pending' ? 'sent,in_progress' : 'completed';
        dispatch(
            listFormInvitationItemsThunk(apiClientWrapper, {
                page,
                per: size,
                query: searchText.trim() || undefined,
                status: statusParam,
                customerId: String(clientId)
            })
        );
    };

    const renderContent = () => {
        // If we're viewing a completed form, render page-level view similar to Intake Form
        if (viewingRecord) {
            return (
                <div className={styles.root}>
                    <div className={styles.tableContainer}>
                        <ViewCompletedForm
                            record={viewingRecord}
                            data={viewingData}
                            loading={viewingLoading}
                            onBack={() => {
                                setViewingRecord(null);
                                setViewingData(null);
                                setViewingLoading(false);
                            }}
                            client={client}
                            onBook={onBook}
                            onNewSale={onNewSale}
                            onClose={onClose}
                            onSelect={onSelect}
                            hideClientHeader={hideClientHeader}
                        />
                    </div>
                </div>
            );
        }
        if (initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }
        return (
            <div className={styles.root}>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={'Forms'}
                        buttonLabel={'Send Form'}
                        displayButton={true}
                        onClick={handleShowSendForms}
                        id="forms_client_send_button"
                        className={styles.headerContainer}
                    />

                    <SelectionBar
                        options={[
                            { id: 'pending', name: 'Pending' },
                            { id: 'completed', name: 'Completed' }
                        ]}
                        selectedId={tab}
                        onSelect={id => {
                            setTab(id as 'pending' | 'completed');
                            setPage(1);
                        }}
                        className={styles.selectionBar}
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={v => {
                            setSearchText(v);
                            setPage(1);
                        }}
                        placeholder="Search by form name"
                        className={styles.searchBarContainer}
                    />
                </div>

                <div className={styles.tableContainer}>
                    <ClientFormsTable
                        records={records}
                        status={tab}
                        isLoading={loadingFromSlice}
                        isEmpty={!loadingFromSlice && records.length === 0}
                        pagination={{ page: currentPage || page, totalPages, size: pageSizeFromSlice }}
                        onPageChange={setPage}
                        onSizeChange={(newSize: number) => {
                            setSize(newSize);
                            setPage(1);
                        }}
                        onView={async rec => {
                            setViewingRecord(rec);
                            setViewingLoading(true);
                            setViewingData(null);
                            try {
                                const invitationId = (rec as any).id;
                                if (invitationId) {
                                    const full = await (dispatch as any)(
                                        (getFormInvitationItemThunk as any)(apiClientWrapper, String(invitationId))
                                    );
                                    const payload = full && (full as any).data ? (full as any).data : full;
                                    setViewingData((payload as any) || null);
                                }
                            } catch (e) {
                                // Silent fail – UI will show no responses state
                            } finally {
                                setViewingLoading(false);
                            }
                        }}
                    />
                </div>
                {showSendForms && (
                    <SendForms
                        client={client}
                        onClose={handleCloseShareSendClients}
                        onSent={handleSendForms}
                        onNavigateToClientProfile={() => onSelect('client')}
                    />
                )}
            </div>
        );
    };

    return renderContent();
};

export default ClientForms;
